/**
 * Centralized SVG Configuration
 * Define all custom SVGs here to use them throughout the application
 */

export type CustomSvgName =
	| 'account-circle'
	| 'article'
	| 'bar-chart-custom'
	| 'computer'
	| 'contract-edit'
	| 'developer-guide'
	| 'emergency-home'
	| 'encrypted'
	| 'error'
	| 'file-save-off'
	| 'google-icon'
	| 'in-home-mode'
	| 'instagram-icon'
	| 'linkedin-icon'
	| 'logo-white'
	| 'logo'
	| 'published-with-changes'
	| 'star-custom'
	| 'verified-custom'
	| 'youtube-icon'
	| 'dashboard-icon-home'
	| 'dashboard-icon-article_person'
	| 'dashboard-icon-credit_card'
	| 'dashboard-icon-description'
	| 'dashboard-icon-holiday_village'
	| 'dashboard-icon'
	| 'properties-icon'
	| 'settings'
	| 'users'
	| 'verified'
	| 'reset'
	| 'admin-user'
	| 'tenent-user'
	| 'role-manage'
	| 'system-setting'
	| 'smoking-allowed'
	| 'non-smoking-allowed'
	| 'pets-allowed'
	| 'included'
	| 'not-available'
	| 'available-at-cost'
	| 'parking'
	| 'laundryunit'
	| 'laundryinbuild'
	| 'elevator'
	| 'gym'
	| 'pool'
	| 'storagelocker'
	| 'bikestorage'
	| 'electricity'
	| 'water'
	| 'heat'
	| 'gas'
	| 'internet'
	| 'consofees'
	| 'ownpay'
	| 'multifamily'
	| 'singlefamily'
	| 'petnotallowed'
	| 'other-amenities'
	| 'accept'
	| 'decline'
	| 'request'
	| 'widthdraw'
	| 'abandon'
	| 'arrow-right'
	| 'user-group'
	// Sidebar nav icons — normal + active pair per item (files under public/img).
	| 'nav-dashboard'
	| 'nav-dashboard-active'
	| 'nav-applications'
	| 'nav-applications-active'
	| 'nav-user-management'
	| 'nav-user-management-active'
	| 'nav-properties'
	| 'nav-properties-active'
	| 'nav-roles'
	| 'nav-roles-active'
	| 'nav-settings'
	| 'nav-settings-active'
	| 'nav-support'
	| 'nav-support-active'
	| 'nav-audit-log'
	| 'nav-audit-log-active'
	| 'nav-billing'
	| 'nav-billing-active'
	| 'nav-notifications'
	| 'nav-notifications-active'
	| 'nav-leases'
	| 'nav-leases-active'

/**
 * Map of custom SVG names to their paths in the public directory
 */
export const customSvgPaths: Record<CustomSvgName, string> = {
	'account-circle': '/img/account_circle.svg',
	article: '/img/article.svg',
	'bar-chart-custom': '/img/bar_chart.svg',
	computer: '/img/computer.svg',
	'contract-edit': '/img/contract_edit.svg',
	'developer-guide': '/img/developer_guide.svg',
	'emergency-home': '/img/emergency_home.svg',
	encrypted: '/img/encrypted.svg',
	error: '/img/error.svg',
	'file-save-off': '/img/file_save_off.svg',
	'google-icon': '/img/google-icon.svg',
	'in-home-mode': '/img/in_home_mode.svg',
	'instagram-icon': '/img/instagram-icon.svg',
	'linkedin-icon': '/img/linkedin-icon.svg',
	'logo-white': '/img/logo-white.svg',
	logo: '/img/logo.svg',
	'published-with-changes': '/img/published_with_changes.svg',
	'star-custom': '/img/star_icon.svg',
	'verified-custom': '/img/verified.svg',
	'youtube-icon': '/img/youtube-icon.svg',
	'dashboard-icon-home': '/img/dashboard-square.svg',
	'dashboard-icon-article_person': '/img/dashboard-icon-article_person.svg',
	'dashboard-icon-credit_card': '/img/dashboard-icon-credit_card.svg',
	'dashboard-icon-description': '/img/profile-avatar.png',
	'dashboard-icon-holiday_village': '/img/properties-icon.svg',
	'dashboard-icon': '/img/dashboard-square.svg',
	'properties-icon': '/img/properties-nav-icon.png',
	settings: '/img/settings.svg',
	users: '/img/admin-user.svg',
	verified: '/img/varified.svg',
	reset: '/img/reset.svg',
	'admin-user': '/img/admin-user.svg',
	'tenent-user': '/img/tenent-user.svg',
	'role-manage': '/img/role-manage.svg',
	'system-setting': '/img/system-setting.svg',
	'smoking-allowed': '/img/smoking.svg',
	'non-smoking-allowed': '/img/nonSmoking.svg',
	'pets-allowed': '/img/pets-allowed.svg',
	included: '/img/included.svg',
	'not-available': '/img/no-available.svg',
	'available-at-cost': '/img/available-at-cost.svg',
	parking: '/img/parking.svg',
	laundryunit: '/img/laundryunit.svg',
	laundryinbuild: '/img/laundryinbuild.svg',
	elevator: '/img/elevator.svg',
	gym: '/img/gym.svg',
	pool: '/img/pool.svg',
	storagelocker: '/img/storagelocker.svg',
	bikestorage: '/img/bikestorage.svg',
	electricity: '/img/electricity.svg',
	water: '/img/water.svg',
	heat: '/img/heat.svg',
	gas: '/img/gas.svg',
	internet: '/img/internet.svg',
	consofees: '/img/consofees.svg',
	ownpay: '/img/ownpay.svg',
	multifamily: '/img/multifamily.svg',
	singlefamily: '/img/singlefamily.svg',
	petnotallowed: '/img/petnoallowed.svg',
	'other-amenities': '/img/other-amenities.svg',
	accept: '/img/accept.svg',
	decline: '/img/decline.svg',
	request: '/img/request.svg',
	widthdraw: '/img/widthdraw.svg',
	abandon: '/img/abandon.svg',
	'arrow-right': '/img/arrow-right.svg',
	'user-group': '/img/user-group.svg',
	// Sidebar nav icons (normal / active). Filenames match the assets provided.
	'nav-dashboard': '/img/dashboard.svg',
	'nav-dashboard-active': '/img/activedashboard.svg',
	'nav-applications': '/img/allapplications.svg',
	'nav-applications-active': '/img/activeallapplication.svg',
	'nav-user-management': '/img/managment.svg',
	'nav-user-management-active': '/img/activemanagment.svg',
	'nav-properties': '/img/properties.svg',
	'nav-properties-active': '/img/activeproperties.svg',
	'nav-roles': '/img/permission.svg',
	'nav-roles-active': '/img/activepermission.svg',
	'nav-settings': '/img/settings.svg',
	'nav-settings-active': '/img/activesettings.svg',
	'nav-support': '/img/support.svg',
	'nav-support-active': '/img/activesupport.svg',
	'nav-audit-log': '/img/log.svg',
	'nav-audit-log-active': '/img/activelog.svg',
	'nav-billing': '/img/billing.svg',
	'nav-billing-active': '/img/activebilling.svg',
	'nav-notifications': '/img/notification.svg',
	'nav-notifications-active': '/img/activenotification.svg',
	'nav-leases': '/img/leases.svg',
	'nav-leases-active': '/img/activeleases.svg',
}

/**
 * Get the path for a custom SVG
 */
export function getCustomSvgPath(name: CustomSvgName): string {
	return customSvgPaths[name]
}

/**
 * Check if a name is a custom SVG
 */
export function isCustomSvg(name: string): name is CustomSvgName {
	return name in customSvgPaths
}
